(function () {
  if (!window.HbElements || !window.subscribe || !window.publish) return;

  class HbCollectionSort extends HbElements.Component {
    onConnect() {
      this.select = this.querySelector('[data-sort-select]');
      this.sectionId = this.dataset.sectionId;
      this.onChange = this.onChange.bind(this);
      if (this.select) this.select.addEventListener('change', this.onChange);
    }

    onDisconnect() {
      if (this.select) this.select.removeEventListener('change', this.onChange);
    }

    onChange() {
      if (!this.select) return;
      publish(PUB_SUB_EVENTS.collectionSortChange, {
        sortBy: this.select.value,
        sectionId: this.sectionId,
      });
    }
  }

  // Sub-collection chips. They live in a different section than the grid, so the
  // selected collection is broadcast rather than addressed to a section id.
  class HbCollectionChildNav extends HbElements.Component {
    onConnect() {
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
    }

    onDisconnect() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
    }

    onClick(event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const chip = event.target.closest('[data-collection-url]');
      if (!chip || !this.contains(chip)) return;
      if (!document.querySelector('hb-product-grid[data-collection-handle]')) return;

      event.preventDefault();
      this.setActive(chip);
      publish(PUB_SUB_EVENTS.collectionChange, { url: chip.dataset.collectionUrl });
    }

    onPopState() {
      const chip = this.querySelector(`[data-collection-url="${window.location.pathname}"]`);
      if (chip) this.setActive(chip);
    }

    setActive(chip) {
      const outline = this.dataset.activeStyle === 'outline';
      this.querySelectorAll('[data-collection-url]').forEach((el) => {
        const isActive = el === chip;
        el.classList.toggle('hb-collection-child-nav__chip--active', isActive);
        el.classList.toggle('hb-collection-child-nav__chip--outline-active', isActive && outline);
        if (isActive) {
          el.setAttribute('aria-current', 'page');
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }
  }

  class HbProductGrid extends HbElements.Component {
    onConnect() {
      this.sectionId = this.dataset.sectionId;
      this.blockId = this.dataset.blockId;
      this.paginationType = this.dataset.paginationType || 'pagination';
      this.loading = false;
      this.observer = null;

      this.onSortChange = this.onSortChange.bind(this);
      this.onFilterChange = this.onFilterChange.bind(this);
      this.onPageChange = this.onPageChange.bind(this);
      this.onCollectionChange = this.onCollectionChange.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onPaginationClick = this.onPaginationClick.bind(this);

      this.unsubscribeSort = subscribe(PUB_SUB_EVENTS.collectionSortChange, this.onSortChange);
      this.unsubscribeFilter = subscribe(PUB_SUB_EVENTS.collectionFilterChange, this.onFilterChange);
      this.unsubscribePage = subscribe(PUB_SUB_EVENTS.collectionPageChange, this.onPageChange);
      this.unsubscribeCollection = subscribe(
        PUB_SUB_EVENTS.collectionChange,
        this.onCollectionChange
      );
      window.addEventListener('popstate', this.onPopState);

      this.bindPagination();
      this.setupInfinite();
    }

    onDisconnect() {
      if (this.unsubscribeSort) this.unsubscribeSort();
      if (this.unsubscribeFilter) this.unsubscribeFilter();
      if (this.unsubscribePage) this.unsubscribePage();
      if (this.unsubscribeCollection) this.unsubscribeCollection();
      window.removeEventListener('popstate', this.onPopState);
      this.teardownInfinite();
      this.unbindPagination();
    }

    bindPagination() {
      this.pagination = this.querySelector('[data-pagination]');
      if (!this.pagination) return;
      this.pagination.addEventListener('click', this.onPaginationClick);
    }

    unbindPagination() {
      if (!this.pagination) return;
      this.pagination.removeEventListener('click', this.onPaginationClick);
    }

    onPaginationClick(event) {
      const link = event.target.closest('[data-page]');
      if (!link || !this.pagination.contains(link)) return;
      if (link.getAttribute('aria-current') === 'page') return;
      event.preventDefault();
      const page = Number(link.dataset.page);
      if (!Number.isFinite(page) || page < 1) return;
      publish(PUB_SUB_EVENTS.collectionPageChange, {
        page,
        sectionId: this.sectionId,
        mode: link.dataset.mode || 'replace',
      });
    }

    setupInfinite() {
      this.teardownInfinite();
      if (this.paginationType !== 'infinite') return;
      const sentinel = this.querySelector('[data-sentinel]');
      if (!sentinel) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry || !entry.isIntersecting || this.loading) return;
          const nextPage = Number(sentinel.dataset.nextPage);
          if (!Number.isFinite(nextPage) || nextPage < 2) return;
          this.render({ page: nextPage, mode: 'append' });
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(sentinel);
    }

    teardownInfinite() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    onSortChange(event) {
      if (event.sectionId && event.sectionId !== this.sectionId) return;
      this.render({
        sortBy: event.sortBy,
        page: 1,
        mode: 'replace',
      });
    }

    onFilterChange(event) {
      if (event.sectionId && event.sectionId !== this.sectionId) return;
      this.render({
        search: event.search,
        page: 1,
        mode: 'replace',
      });
    }

    onPageChange(event) {
      if (event.sectionId && event.sectionId !== this.sectionId) return;
      const mode = event.mode || (this.paginationType === 'infinite' ? 'append' : 'replace');
      this.render({
        page: event.page,
        mode,
      });
    }

    onCollectionChange(event) {
      if (!event.url) return;
      this.render({
        path: event.url,
        page: 1,
        mode: 'replace',
      });
    }

    onPopState() {
      this.render({
        mode: 'replace',
        updateUrl: false,
      });
    }

    currentParams() {
      return new URLSearchParams(window.location.search);
    }

    // The path whose products are on screen. A sub-collection chip swaps this without
    // touching the URL, so every later fetch has to use it rather than location.pathname.
    get collectionPath() {
      return this.dataset.collectionPath || window.location.pathname;
    }

    startCollectionUpdate(params, page) {
      const { CollectionUpdateEvent } = window.StandardEvents || {};
      if (!CollectionUpdateEvent) return null;
      if (!this.dataset.collectionHandle && this.dataset.collectionId == null) return null;

      const deferred = CollectionUpdateEvent.createPromise();
      const payload = {
        collection: {
          id: this.dataset.collectionId || null,
          handle: this.dataset.collectionHandle || '',
          productsCount: Number(this.dataset.productsCount) || 0,
        },
        productFilters: CollectionUpdateEvent.parseProductFilters(params),
        sortKey: CollectionUpdateEvent.getSortKey(params),
        promise: deferred.promise,
      };
      if (page != null) payload.detail = { page };

      this.dispatchEvent(new CollectionUpdateEvent(payload));
      return deferred;
    }

    async render({ sortBy, page, mode, updateUrl, search, path } = {}) {
      if (this.loading) return;
      this.setLoading(true);

      const nextPath = path || this.collectionPath;
      const params = search != null ? new URLSearchParams(search) : this.currentParams();
      if (path) {
        // Facet values belong to the collection they were rendered from; sort survives.
        params.delete('page');
        Array.from(params.keys())
          .filter((key) => key.startsWith('filter.'))
          .forEach((key) => params.delete(key));
      }
      if (sortBy != null) params.set('sort_by', sortBy);
      if (page != null) params.set('page', String(page));
      if (mode === 'replace' && (sortBy != null || search != null)) params.set('page', '1');

      const requestParams = new URLSearchParams(params);
      requestParams.set('section_id', this.dataset.renderSectionId || this.sectionId);

      const requestUrl = `${nextPath}?${requestParams.toString()}`;
      const updateEvent = this.startCollectionUpdate(params, page);

      try {
        const tagName = this.tagName.toLowerCase();
        const selector = `${tagName}[data-block-id="${this.blockId}"]`;
        const fallbackSelector = `${tagName}[data-collection-handle]`;
        let nextGrid = null;

        const response = await fetch(requestUrl);
        if (response.ok) {
          const text = await response.text();
          if (text && text.trim()) {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            nextGrid = doc.querySelector(selector) || doc.querySelector(fallbackSelector);
          }
        }

        if (!nextGrid) {
          const fallbackParams = new URLSearchParams(params);
          fallbackParams.delete('section_id');
          const fallbackQuery = fallbackParams.toString();
          const fallbackUrl = fallbackQuery ? `${nextPath}?${fallbackQuery}` : nextPath;
          const fallbackResponse = await fetch(fallbackUrl);
          if (!fallbackResponse.ok) throw new Error(`Collection render failed: ${fallbackResponse.status}`);
          const fallbackText = await fallbackResponse.text();
          const fallbackDoc = new DOMParser().parseFromString(fallbackText, 'text/html');
          nextGrid = fallbackDoc.querySelector(selector) || fallbackDoc.querySelector(fallbackSelector);
        }

        if (!nextGrid) throw new Error('Collection grid markup not found');

        const productsCount = Number(
          nextGrid.dataset.productsCount || this.dataset.productsCount || 0
        );
        if (updateEvent) updateEvent.resolve({ productsCount });

        if (mode === 'append') {
          this.appendFrom(nextGrid);
          if (updateUrl !== false && nextPath === window.location.pathname) {
            // Appended pages stay out of the URL: reloading on ?page=3 would drop pages 1-2.
            const appendParams = new URLSearchParams(params);
            appendParams.delete('page');
            this.pushUrl(appendParams);
          }
          this.setLoading(false);
          return;
        }

        // A sub-collection switch never touches the URL, not even when it lands back on
        // the collection the page itself renders.
        if (updateUrl !== false && !path && nextPath === window.location.pathname) {
          this.pushUrl(params);
        }
        const imported = document.importNode(nextGrid, true);
        // Keep this block's own ids so pub/sub and pagination stay stable even when the
        // markup came from a collection on a different template.
        imported.dataset.sectionId = this.sectionId;
        imported.dataset.blockId = this.blockId;
        if (nextPath === window.location.pathname) {
          delete imported.dataset.collectionPath;
          delete imported.dataset.renderSectionId;
        } else {
          imported.dataset.collectionPath = nextPath;
          imported.dataset.renderSectionId = nextGrid.dataset.sectionId || '';
        }
        this.replaceWith(imported);
        if (page != null && !path) {
          this.scrollGridIntoView(imported);
        }
      } catch (error) {
        console.error(error);
        if (updateEvent) updateEvent.reject(error);
        this.setLoading(false);
      }
    }

    scrollGridIntoView(grid) {
      if (!grid) return;
      const headerHeight = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      const offset = Number.isFinite(headerHeight) ? headerHeight : 0;
      const top = grid.getBoundingClientRect().top + window.scrollY - offset;
      const reducedMotion =
        (window.HbElements && typeof window.HbElements.prefersReducedMotion === 'function'
          ? window.HbElements.prefersReducedMotion()
          : false) || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.classList.toggle('is-loading', isLoading);
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      const loadingEl = this.querySelector('[data-loading]');
      if (loadingEl) {
        loadingEl.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
      }
    }

    pushUrl(params) {
      const pushParams = new URLSearchParams(params);
      pushParams.delete('section_id');
      const query = pushParams.toString();
      const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
      history.pushState({ searchParams: query }, '', nextUrl);
    }

    appendFrom(nextGrid) {
      const list =
        this.querySelector('[data-list]') || this.querySelector('.hb-product-grid__list');
      const nextList =
        nextGrid.querySelector('[data-list]') || nextGrid.querySelector('.hb-product-grid__list');
      if (list && nextList) {
        Array.from(nextList.children).forEach((child) => {
          list.appendChild(child);
        });
      }

      this.dataset.currentPage = nextGrid.dataset.currentPage || this.dataset.currentPage;
      this.dataset.totalPages = nextGrid.dataset.totalPages || this.dataset.totalPages;

      const oldPagination = this.querySelector('[data-pagination]');
      const nextPagination = nextGrid.querySelector('[data-pagination]');
      if (oldPagination && nextPagination) {
        oldPagination.replaceWith(nextPagination.cloneNode(true));
        this.unbindPagination();
        this.bindPagination();
      } else if (oldPagination && !nextPagination) {
        oldPagination.remove();
        this.unbindPagination();
      }

      const oldSentinel = this.querySelector('[data-sentinel]');
      if (oldSentinel) oldSentinel.remove();

      const nextSentinel = nextGrid.querySelector('[data-sentinel]');
      if (nextSentinel) {
        this.appendChild(nextSentinel.cloneNode(true));
      }

      this.teardownInfinite();
      this.setupInfinite();
    }
  }

  class HbCollectionFacets extends HbElements.Component {
    onConnect() {
      this.sectionId = this.dataset.sectionId;
      this.blockId = this.dataset.blockId;
      this.form = this.querySelector('[data-facets-form]');
      this.loading = false;
      this.priceTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onFormChange = this.onFormChange.bind(this);
      this.onPriceInput = this.onPriceInput.bind(this);
      this.onFilterChange = this.onFilterChange.bind(this);
      this.onSortChange = this.onSortChange.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick);
      if (this.form) {
        this.form.addEventListener('change', this.onFormChange);
        this.form.addEventListener('input', this.onPriceInput);
      }
      this.unsubscribeFilter = subscribe(PUB_SUB_EVENTS.collectionFilterChange, this.onFilterChange);
      this.unsubscribeSort = subscribe(PUB_SUB_EVENTS.collectionSortChange, this.onSortChange);
      window.addEventListener('popstate', this.onPopState);
    }

    onDisconnect() {
      this.removeEventListener('click', this.onClick);
      if (this.form) {
        this.form.removeEventListener('change', this.onFormChange);
        this.form.removeEventListener('input', this.onPriceInput);
      }
      if (this.unsubscribeFilter) this.unsubscribeFilter();
      if (this.unsubscribeSort) this.unsubscribeSort();
      window.removeEventListener('popstate', this.onPopState);
      if (this.priceTimer) clearTimeout(this.priceTimer);
    }

    onClick(event) {
      const allButton = event.target.closest('[data-facet-all]');
      if (allButton && this.contains(allButton)) {
        event.preventDefault();
        this.querySelectorAll('[data-facet-chip]').forEach((input) => {
          input.checked = false;
        });
        this.applyFilters();
        return;
      }

      const clearButton = event.target.closest('[data-facet-clear]');
      if (clearButton && this.contains(clearButton)) {
        event.preventDefault();
        if (this.form) this.form.reset();
        this.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.checked = false;
        });
        this.querySelectorAll('[data-facet-price]').forEach((input) => {
          input.value = '';
        });
        this.applyFilters();
      }
    }

    onFormChange(event) {
      if (!event.target) return;
      if (event.target.matches('[data-facet-price]')) return;
      this.syncExclusiveChips(event.target);
      this.applyFilters();
    }

    onPriceInput(event) {
      if (!event.target || !event.target.matches('[data-facet-price]')) return;
      if (this.priceTimer) clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => this.applyFilters(), 400);
    }

    syncExclusiveChips(target) {
      if (!target.matches('[data-facet-chip]')) return;
      if (!target.checked) return;
      if (!this.boolAttr('chipExclusive', true)) return;
      const name = target.getAttribute('name');
      if (!name) return;
      this.querySelectorAll('[data-facet-chip]').forEach((input) => {
        if (input === target) return;
        if (input.getAttribute('name') === name) input.checked = false;
      });
    }

    formParams() {
      const params = new URLSearchParams();
      const current = new URLSearchParams(window.location.search);
      const sortBy = current.get('sort_by');
      if (sortBy) params.set('sort_by', sortBy);

      if (!this.form) return params;
      const formData = new FormData(this.form);
      formData.forEach((value, key) => {
        if (value == null) return;
        const trimmed = String(value).trim();
        if (!trimmed) return;
        params.append(key, trimmed);
      });
      return params;
    }

    applyFilters() {
      if (this.loading) return;
      const params = this.formParams();
      publish(PUB_SUB_EVENTS.collectionFilterChange, {
        search: params.toString(),
        sectionId: this.sectionId,
        blockId: this.blockId,
      });
    }

    onFilterChange(event) {
      if (event.sectionId && event.sectionId !== this.sectionId) return;
      const params =
        event.search != null ? new URLSearchParams(event.search) : new URLSearchParams(window.location.search);
      params.set('page', '1');
      this.refresh(params);
    }

    onSortChange(event) {
      if (event.sectionId && event.sectionId !== this.sectionId) return;
      const params = new URLSearchParams(window.location.search);
      if (event.sortBy) params.set('sort_by', event.sortBy);
      params.set('page', '1');
      this.refresh(params);
    }

    onPopState() {
      this.refresh(new URLSearchParams(window.location.search));
    }

    async refresh(params) {
      if (this.loading) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');

      const requestParams = new URLSearchParams(params);
      requestParams.set('section_id', this.sectionId);
      requestParams.delete('page');
      requestParams.set('page', params.get('page') || '1');

      const requestUrl = `${window.location.pathname}?${requestParams.toString()}`;
      const selector = `hb-collection-facets[data-block-id="${this.blockId}"]`;

      try {
        const response = await fetch(requestUrl);
        if (!response.ok) throw new Error(`Facets render failed: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = doc.querySelector(selector);
        if (!next) throw new Error('Facets markup not found');
        this.replaceWith(document.importNode(next, true));
      } catch (error) {
        console.error(error);
        this.loading = false;
        this.setAttribute('aria-busy', 'false');
      }
    }
  }

  // Merged multi-blog grids cannot use Shopify pagination, so the full set is in
  // the DOM and Load more reveals the next batch.
  class HbArticleGrid extends HbProductGrid {
    onConnect() {
      if (this.dataset.paginationType === 'reveal') {
        this.setupReveal();
        return;
      }
      super.onConnect();
    }

    onDisconnect() {
      if (this.dataset.paginationType === 'reveal') {
        if (this.moreButton) this.moreButton.removeEventListener('click', this.onRevealMore);
        return;
      }
      super.onDisconnect();
    }

    setupReveal() {
      this.revealStep = Math.max(parseInt(this.dataset.revealStep || '6', 10), 1);
      this.revealItems = Array.from(this.querySelectorAll('[data-list] > .hb-article-grid__item'));
      this.revealed = this.revealStep;
      this.moreButton = this.querySelector('[data-reveal-more]');
      this.onRevealMore = this.onRevealMore.bind(this);
      if (this.moreButton) this.moreButton.addEventListener('click', this.onRevealMore);
      this.applyReveal();
    }

    onRevealMore(event) {
      event.preventDefault();
      this.revealed += this.revealStep;
      this.applyReveal();
      const next = this.revealItems[this.revealed - this.revealStep];
      if (next) next.querySelector('a, [tabindex]')?.focus?.();
    }

    applyReveal() {
      this.revealItems.forEach((item, index) => {
        item.hidden = index >= this.revealed;
      });
      const pagination = this.querySelector('[data-pagination]');
      if (pagination) pagination.hidden = this.revealed >= this.revealItems.length;
    }
  }

  HbElements.define('hb-collection-child-nav', HbCollectionChildNav);
  HbElements.define('hb-collection-sort', HbCollectionSort);
  HbElements.define('hb-collection-facets', HbCollectionFacets);
  HbElements.define('hb-product-grid', HbProductGrid);
  HbElements.define('hb-article-grid', HbArticleGrid);
})();
